import {
  BOTTOM_PAD,
  GAP_MAIN,
  GAP_STACK,
  LABEL_Y,
  LANE_GAP,
  MIN_FULL,
  MIN_MID,
  MIN_RIBBON,
  RULE_Y,
  X0,
  Y0,
} from './constants';
import type {
  LaidOutColumn,
  LaidOutGate,
  LaidOutNode,
  LaidOutRibbon,
  Layout,
  NodeTier,
  ViewSpec,
} from './types';

/**
 * A closed ribbon: a cubic from (sx, sy) to (tx, ty), across by `height`, and
 * back. Thickness is people at true scale on both ends.
 */
export function ribbonPath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  height: number,
): string {
  const bend = (tx - sx) * 0.52;
  return (
    `M ${sx} ${sy}` +
    ` C ${sx + bend} ${sy}, ${tx - bend} ${ty}, ${tx} ${ty}` +
    ` L ${tx} ${ty + height}` +
    ` C ${tx - bend} ${ty + height}, ${sx + bend} ${sy + height}, ${sx} ${sy + height}` +
    ' Z'
  );
}

/**
 * An overlay connector: a single open curve, never a closed band. Between
 * columns it arcs across; within a column it drops from the source's underside
 * to the target's top, so it reads as "hangs off this" rather than "flows on".
 */
function connectorPath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  sameColumn: boolean,
): string {
  if (sameColumn) {
    const mid = (sy + ty) / 2;
    return `M ${sx} ${sy} C ${sx} ${mid}, ${tx} ${mid}, ${tx} ${ty}`;
  }
  const bend = (tx - sx) * 0.52;
  return `M ${sx} ${sy} C ${sx + bend} ${sy}, ${tx - bend} ${ty}, ${tx} ${ty}`;
}

function tierFor(height: number): NodeTier {
  if (height >= MIN_FULL) return 'full';
  if (height >= MIN_MID) return 'mid';
  return 'compact';
}

/**
 * Divide a fixed height by value while respecting the cards' readable floors.
 * When a small group hits its floor, the remaining height is redistributed
 * proportionally among the groups that are still free to grow.
 */
function proportionalPartition(
  total: number,
  values: number[],
  minimums: number[],
): number[] {
  const heights = Array.from({ length: values.length }, () => 0);
  const active = new Set(values.map((_, i) => i));
  let remaining = Math.max(total, minimums.reduce((sum, minimum) => sum + minimum, 0));

  while (active.size > 0) {
    const activeIndices = [...active];
    const weight = activeIndices.reduce((sum, i) => sum + Math.max(0, values[i] ?? 0), 0);
    const equalShare = weight <= 1e-9;
    const belowMinimum = activeIndices.filter((i) => {
      const share = equalShare
        ? 1 / activeIndices.length
        : Math.max(0, values[i] ?? 0) / weight;
      return remaining * share + 1e-9 < (minimums[i] ?? 0);
    });

    if (belowMinimum.length > 0) {
      for (const i of belowMinimum) {
        const minimum = minimums[i] ?? 0;
        heights[i] = minimum;
        remaining -= minimum;
        active.delete(i);
      }
      continue;
    }

    const finalWeight = activeIndices.reduce(
      (sum, i) => sum + Math.max(0, values[i] ?? 0),
      0,
    );
    for (const i of activeIndices) {
      heights[i] =
        finalWeight <= 1e-9
          ? remaining / activeIndices.length
          : remaining * (Math.max(0, values[i] ?? 0) / finalWeight);
    }
    break;
  }

  return heights;
}

/**
 * Pure geometry: a view spec in, positioned nodes and ribbon paths out.
 *
 * Lanes stack vertically. Within a lane each column stacks its own nodes with
 * the retained path on top and everything it sheds directly below. Ribbons
 * leave a node as one contiguous stack centerd on its card, and always carry
 * the true value even when the card itself is held at a minimum height.
 */
export function computeLayout(spec: ViewSpec): Layout {
  const byKey = new Map(spec.nodes.map((n) => [n.key, n]));

  /* Each node's denominator: the thing it came from, or the view's base. */
  const parentOf = new Map<string, { value: number; label: string }>();
  for (const flow of spec.flows) {
    const from = byKey.get(flow.from);
    for (const key of flow.children) {
      parentOf.set(key, { value: from?.value ?? 0, label: flow.denominatorLabel });
    }
  }

  const scale = spec.sourceH / Math.max(1e-9, spec.scaleBase);
  const band = (value: number) => Math.max(0, value * scale);

  /* ---- vertical: lane by lane, column by column ---- */
  const geometry = new Map<string, { x: number; y: number; w: number; h: number }>();

  const lanes = new Map<number, typeof spec.nodes>();
  for (const node of spec.nodes) {
    const lane = node.lane ?? 0;
    const bucket = lanes.get(lane);
    if (bucket) bucket.push(node);
    else lanes.set(lane, [node]);
  }
  const laneKeys = [...lanes.keys()].sort((a, b) => a - b);

  let cursor = Y0;
  let firstTop = Y0;
  let lastBottom = Y0;

  laneKeys.forEach((lane, laneIndex) => {
    const top = cursor;
    let bottom = top;

    const byCol = new Map<number, typeof spec.nodes>();
    for (const node of lanes.get(lane) ?? []) {
      if (node.spanLanes) continue;
      const bucket = byCol.get(node.col);
      if (bucket) bucket.push(node);
      else byCol.set(node.col, [node]);
    }

    for (const column of byCol.values()) {
      let y = top;
      column.forEach((node, i) => {
        const h = Math.max(node.minH ?? (i === 0 ? MIN_FULL : MIN_MID), band(node.value));
        geometry.set(node.key, { x: 0, y, w: 0, h });
        y += h + (i === 0 ? GAP_MAIN : GAP_STACK);
      });
      bottom = Math.max(bottom, y - GAP_STACK);
    }

    if (laneIndex === 0) firstTop = top;
    lastBottom = bottom;
    cursor = bottom + LANE_GAP;
  });

  /* A source that feeds every lane spans the full stack. */
  for (const node of spec.nodes) {
    if (!node.spanLanes) continue;
    geometry.set(node.key, {
      x: 0,
      y: firstTop,
      w: 0,
      h: Math.max(MIN_FULL, lastBottom - firstTop),
    });
  }

  /* A proportional child column fills its spanning parent, less lane gaps. */
  for (const flow of spec.flows) {
    if (!flow.partitionChildren) continue;
    const from = geometry.get(flow.from);
    const children = flow.children.flatMap((key) => {
      const node = byKey.get(key);
      const childGeometry = geometry.get(key);
      return node && childGeometry ? [{ node, geometry: childGeometry }] : [];
    });
    if (!from || children.length === 0) continue;

    const gaps = LANE_GAP * Math.max(0, children.length - 1);
    const minimums = children.map(({ geometry: childGeometry }) => childGeometry.h);
    const minimumTotal = minimums.reduce((sum, minimum) => sum + minimum, 0);
    const usableHeight = Math.max(from.h - gaps, minimumTotal);
    const heights = proportionalPartition(
      usableHeight,
      children.map(({ node }) => node.value),
      minimums,
    );

    from.h = usableHeight + gaps;
    let childY = from.y;
    children.forEach(({ geometry: childGeometry }, i) => {
      childGeometry.y = childY;
      childGeometry.h = heights[i] ?? 0;
      childY += childGeometry.h + LANE_GAP;
    });
  }

  /* ---- horizontal: x from column index ---- */
  const columnX: number[] = [];
  let x = X0;
  for (const column of spec.columns) {
    columnX.push(x);
    x += column.width + spec.gap;
  }
  const totalWidth = x - spec.gap + X0;

  for (const node of spec.nodes) {
    const g = geometry.get(node.key);
    if (!g) continue;
    g.x = columnX[node.col] ?? X0;
    g.w = spec.columns[node.col]?.width ?? 0;
  }

  /* ---- nodes ---- */
  const nodes: LaidOutNode[] = spec.nodes.map((node) => {
    const g = geometry.get(node.key) ?? { x: X0, y: Y0, w: 0, h: MIN_FULL };
    const parent = spec.shareMode === 'parent' ? parentOf.get(node.key) : undefined;
    const share = parent
      ? node.value / Math.max(1e-9, parent.value)
      : node.value / Math.max(1e-9, spec.shareBase);
    return {
      ...node,
      ...g,
      tier: tierFor(g.h),
      share,
      shareLabel: parent ? (spec.parentShareLabel ?? `of ${parent.label}`) : spec.shareLabel,
    };
  });

  /* ---- ribbons ---- */
  const ribbons: LaidOutRibbon[] = [];
  for (const flow of spec.flows) {
    const from = byKey.get(flow.from);
    const fromGeom = from ? geometry.get(from.key) : undefined;
    if (!from || !fromGeom) continue;

    const isOverlay = flow.kind === 'overlay';
    let cursorY = fromGeom.y + Math.max(0, (fromGeom.h - band(from.value)) / 2);

    for (const key of flow.children) {
      const to = byKey.get(key);
      const toGeom = to ? geometry.get(key) : undefined;
      if (!to || !toGeom) continue;

      if (isOverlay) {
        const sameColumn = from.col === to.col;
        const d = sameColumn
          ? connectorPath(
              fromGeom.x + fromGeom.w / 2,
              fromGeom.y + fromGeom.h,
              toGeom.x + toGeom.w / 2,
              toGeom.y,
              true,
            )
          : connectorPath(
              fromGeom.x + fromGeom.w,
              fromGeom.y + fromGeom.h / 2,
              toGeom.x,
              toGeom.y + toGeom.h / 2,
              false,
            );
        ribbons.push({
          key,
          d,
          kind: 'overlay',
          trueThickness: 0,
          thickness: 0,
          tone: to.tone,
          stage: to.stage,
          explode: to.explode,
          title: to.title,
          value: to.value,
          share: to.value / Math.max(1e-9, from.value),
          denominatorLabel: flow.denominatorLabel,
        });
        // An overlay takes nothing with it, so the outflow cursor does not move.
        continue;
      }

      const height = band(to.value);
      const drawn = Math.max(MIN_RIBBON, height);
      const ty = toGeom.y + Math.max(0, (toGeom.h - height) / 2);
      ribbons.push({
        key,
        d: ribbonPath(fromGeom.x + fromGeom.w, cursorY, toGeom.x, ty, drawn),
        kind: 'flow',
        trueThickness: height,
        thickness: drawn,
        tone: to.tone,
        stage: to.stage,
        explode: to.explode,
        title: to.title,
        value: to.value,
        share: to.value / Math.max(1e-9, from.value),
        denominatorLabel: flow.denominatorLabel,
      });
      cursorY += height;
    }
  }

  /* ---- chrome ---- */
  const columns: LaidOutColumn[] = spec.columns.map((column, i) => ({
    label: spec.numbered === false ? column.label : `${i + 1} · ${column.label}`,
    centerX: (columnX[i] ?? X0) + column.width / 2,
  }));

  const gates: LaidOutGate[] = (spec.gates ?? []).flatMap((gate) => {
    const g = geometry.get(gate.after);
    if (!g) return [];
    return [{ key: gate.after, x: g.x + g.w + spec.gap / 2, rate: gate.rate }];
  });

  const difficultyBreak = (() => {
    if (!spec.difficultyBreak) return undefined;
    const g = geometry.get(spec.difficultyBreak.after);
    if (!g) return undefined;
    return {
      x: g.x + g.w + spec.gap / 2,
      label: spec.difficultyBreak.label,
    };
  })();

  const height = nodes.reduce((max, n) => Math.max(max, n.y + n.h), 0) + BOTTOM_PAD;

  return {
    title: spec.title,
    desc: spec.desc,
    palette: spec.palette,
    width: Math.ceil(totalWidth),
    height: Math.ceil(height),
    nodes,
    ribbons,
    columns,
    gates,
    difficultyBreak,
    labelY: LABEL_Y,
    ruleY: RULE_Y,
  };
}
